import { Tab, Tabs } from '@mui/material';
import {
  createLpCapitalActionEditRoute,
  createLpCapitalActionSuccessRoute,
  EQUALISATIONS_ADD,
  LP_CAPITAL_ACTION_CREATE,
  LP_CAPITAL_ACTION_LIST,
} from 'adminConstants/routes';
import Button from 'components/Button';
import ErrorBoundary from 'components/ErrorBoundary';
import Page from 'components/new/Page';
import { Stepper } from 'components/new/Stepper';
import PageLoader from 'components/PageLoader';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionStatus } from 'further-types/lp/action';
import { CapitalActionEntryType } from 'further-types/lp/capital-action';
import { SortOrder } from 'further-types/utils';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import CapitalActionDetails from './CapitalActionDetails';
import CapitalActionTransactions from './CapitalActionEntries';
import CapitalActionReview from './CapitalActionReview';
import useCapitalActionForm, {
  Steps,
  UpdateCapitalActionFormValues,
} from './hooks/useCapitalActionForm';
import useDefaultEntryValues from './hooks/useDefaultEntryValues';
import useGetEntryTitle from './hooks/useGetEntryTitle';

const getStepLabels = (isCreateRoute: boolean): Array<string> => [
  isCreateRoute ? 'Create capital action' : 'Edit capital action',
  'Add transactions',
  'Approve action',
];

const stepToUrlParam = {
  [Steps.Details]: 'details',
  [Steps.Transactions]: 'transactions',
  [Steps.Review]: 'review',
};

const urlParamToStep = {
  details: Steps.Details,
  transactions: Steps.Transactions,
  review: Steps.Review,
};

type StepUrlParam = keyof typeof urlParamToStep;

const isValidStepParam = (param: string | null): param is StepUrlParam =>
  param !== null && param in urlParamToStep;

type Props = {
  vehicleId: string;
  vehicleName: string;
  capitalAction?: UpdateCapitalActionFormValues;
};

const AddEditCapitalAction: React.FC<Props> = ({
  vehicleId,
  vehicleName,
  capitalAction,
}) => {
  const history = useHistory();
  const location = useLocation();
  const isCreateRoute = !!useRouteMatch(LP_CAPITAL_ACTION_CREATE)?.isExact;
  const { id } = useParams<{ id?: string }>();

  const urlParams = new URLSearchParams(location.search);
  const stepParam = urlParams.get('step');
  const [activeStep, setActiveStep] = useState<Steps>(
    isValidStepParam(stepParam) ? urlParamToStep[stepParam] : Steps.Details,
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const confirmDialog = useDisclosure();

  const [overpaymentPerCommitment] =
    api.capital.overpaymentPerCommitment.useSuspenseQuery({
      vehicleId,
    });

  const [listUncalledEqualisations] =
    api.capital.listUncalledEqualisations.useSuspenseQuery({
      vehicleId,
    });

  const [uncalledPartnerTransfers] =
    api.capital.listUncalledPartnerTransfers.useSuspenseQuery({
      vehicleId,
    });

  const [draftCapitalActions] = api.capitalAction.listDrafts.useSuspenseQuery({
    vehicleId,
  });

  const [commitments] = api.commitments.list.useSuspenseQuery({
    vehicleId,
    order: SortOrder.Desc,
    page: 1,
    rowsPerPage: 1000,
    orderBy: 'commitmentDate',
  });
  const hasUncalledEqualisations = (listUncalledEqualisations?.length ?? 0) > 0;
  const hasUncalledPartnerTransfers =
    (uncalledPartnerTransfers?.length ?? 0) > 0;

  const { formMethods, submitStep, isLoading, lastAction, setLastAction } =
    useCapitalActionForm({
      id,
      vehicleId,
      defaultValues: capitalAction,
      hasUncalledEqualisations,
      hasUncalledPartnerTransfers,
    });

  const { append, fields, remove } = useFieldArray({
    control: formMethods.control,
    name: 'entries',
  });
  const { defaultEntry } = useDefaultEntryValues();
  const { hasEqualisationInDraftState } = useEqualisations(vehicleId);
  const getEntryTitle = useGetEntryTitle();

  const capitalActionEntries = formMethods.watch('entries') ?? [];
  const hasDistributionEntry = capitalActionEntries.some(
    (entry) => entry.entryType === CapitalActionEntryType.CapitalDistribution,
  );

  const hasPendingCommitments = commitments.results?.some(
    (commitment) => !commitment.equalisationId,
  );

  const commitmentsPendingDialog = useDisclosure(
    !id && activeStep === Steps.Details && hasPendingCommitments,
  );

  const draftDeletionDialog = useDisclosure(
    isCreateRoute && !id && draftCapitalActions.length > 0,
  );

  const deleteAllDrafts = api.capitalAction.deleteAllDrafts.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Draft capital actions deleted.',
      onSuccess: draftDeletionDialog.onClose,
    }),
  );

  const handleCancel = () => {
    history.push(LP_CAPITAL_ACTION_LIST);
  };

  const handleSaveAndExit = () => {
    setLastAction('saveAndExit');
    submitStep(
      activeStep,
      () => {
        history.push(LP_CAPITAL_ACTION_LIST);
      },
      { approve: false, saveAndExit: true },
    );
  };

  const handleNext = () => {
    setLastAction('saveAndNext');
    submitStep(activeStep, ({ id }) => {
      setActiveStep(activeStep + 1);

      history.push(
        `${createLpCapitalActionEditRoute(id)}?step=${
          stepToUrlParam[activeStep + 1]
        }`,
      );
    });
  };

  const handleSubmit = () => {
    setLastAction('saveAndNext');
    submitStep(activeStep, (capitalAction) => {
      if (capitalAction.status === ActionStatus.Approved) {
        history.push(createLpCapitalActionSuccessRoute(capitalAction.id));
      }
    });
  };

  const handlePrevious = () => {
    setActiveStep(activeStep - 1);

    if (id) {
      history.push(
        `${createLpCapitalActionEditRoute(id)}?step=${
          stepToUrlParam[activeStep - 1]
        }`,
      );
    }
  };

  const handlePreviousTransactions = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    } else {
      handlePrevious();
    }
  };

  const handleAddAnotherTransaction = () => {
    append(defaultEntry);
    setCurrentPageIndex(capitalActionEntries.length);
  };

  const handleRemoveTransaction = () => {
    remove(currentPageIndex);
    if (currentPageIndex >= capitalActionEntries.length - 1) {
      setCurrentPageIndex(Math.max(0, currentPageIndex - 1));
    }
  };

  const allocations = Object.fromEntries(
    capitalAction?.entries?.map((entry, index) => [
      index,
      entry.capitalBreakdown,
    ]) ?? [],
  );

  const hasOverpayments = Object.values(overpaymentPerCommitment ?? {}).some(
    (overpayment) => !!overpayment,
  );

  const messages = hasUncalledEqualisations
    ? [
        {
          key: 'uncalled-equalisations',
          content:
            'This vehicle has an approved equalisation pending call. The equalisation must be the first item added to this capital action.',
          variant: 'info' as const,
          centered: true,
        },
      ]
    : hasOverpayments
    ? [
        {
          key: 'overpayments',
          content:
            'There are overpayments for some investors. Please review the overpayments and adjust the capital action accordingly.',
          variant: 'info' as const,
          centered: true,
        },
      ]
    : [];

  const detailsActionBar = (
    <Row centered spacing="sm" justify="space-between">
      <Button
        size="large"
        variant="outlined"
        onClick={handleCancel}
        disabled={isLoading}
      >
        Cancel
      </Button>
      <Row centered spacing="sm" justify="flex-end">
        <Button
          size="large"
          variant="outlined"
          onClick={handleSaveAndExit}
          loading={lastAction === 'saveAndExit' && isLoading}
          disabled={isLoading}
        >
          Save and exit
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={handleNext}
          loading={lastAction === 'saveAndNext' && isLoading}
          disabled={isLoading}
        >
          Continue
        </Button>
      </Row>
    </Row>
  );

  const transactionsActionBar = (
    <Row centered spacing="sm" justify="space-between" breakpoint="sm">
      <Button
        size="large"
        variant="outlined"
        onClick={handlePreviousTransactions}
        disabled={isLoading}
      >
        Previous step
      </Button>
      <Row centered spacing="sm" justify="flex-end" breakpoint="sm">
        <Button
          size="large"
          variant="outlined"
          onClick={handleSaveAndExit}
          loading={lastAction === 'saveAndExit' && isLoading}
          disabled={isLoading}
        >
          Save and exit
        </Button>
        {capitalActionEntries.length > 1 && (
          <Button
            size="large"
            variant="outlined"
            onClick={handleRemoveTransaction}
            disabled={isLoading}
          >
            Delete transaction
          </Button>
        )}
        <Button
          size="large"
          variant="outlined"
          onClick={handleAddAnotherTransaction}
          disabled={isLoading || hasDistributionEntry}
        >
          Add another transaction type
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={handleNext}
          loading={lastAction === 'saveAndNext' && isLoading}
          disabled={isLoading}
        >
          Go to approve
        </Button>
      </Row>
    </Row>
  );

  const reviewActionBar = (
    <Row centered spacing="sm" justify="space-between">
      <Button
        size="large"
        variant="outlined"
        onClick={handlePrevious}
        disabled={isLoading}
      >
        Previous step
      </Button>
      <Row centered spacing="sm" justify="flex-end">
        <Button
          size="large"
          variant="outlined"
          onClick={handleSaveAndExit}
          loading={lastAction === 'saveAndExit' && isLoading}
          disabled={isLoading}
        >
          Save and exit
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={
            hasEqualisationInDraftState ? confirmDialog.onOpen : handleSubmit
          }
          loading={lastAction === 'saveAndNext' && isLoading}
          disabled={isLoading}
        >
          Save and approve
        </Button>
      </Row>
    </Row>
  );

  const actionBarByStep = {
    [Steps.Details]: detailsActionBar,
    [Steps.Transactions]: transactionsActionBar,
    [Steps.Review]: reviewActionBar,
  };

  return (
    <Page
      heading={`${isCreateRoute ? 'Create' : 'Edit'} capital action`}
      tabTitle={`Capital Actions: ${
        isCreateRoute ? 'Create' : 'Edit'
      } Capital Action`}
      withHeadingDivider={
        !(activeStep === Steps.Transactions && capitalActionEntries.length > 1)
      }
      belowHeading={
        activeStep === Steps.Transactions && capitalActionEntries.length > 1 ? (
          <Tabs
            value={currentPageIndex}
            onChange={(_, newValue: number) => setCurrentPageIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {capitalActionEntries.map((entry, index) => (
              <Tab
                key={index}
                label={getEntryTitle(entry, true)}
                value={index}
              />
            ))}
          </Tabs>
        ) : null
      }
      withStickyHeader
      goBackAction={LP_CAPITAL_ACTION_LIST}
      spacing="lg"
      headerBreakpoint="md"
      customActions={
        <Stepper
          maxWidth="lg"
          activeOnlyBreakpoint="sm"
          steps={getStepLabels(isCreateRoute)}
          activeStep={activeStep}
        />
      }
      actionBar={actionBarByStep[activeStep]}
      messages={messages}
    >
      <Column spacing="lg" fullHeight>
        <FormWithStateContextProvider
          formMethods={formMethods}
          initialState={{ allocations }}
        >
          <ErrorBoundary loader={<PageLoader />}>
            {activeStep === Steps.Details && (
              <CapitalActionDetails vehicle={{ id: vehicleId, vehicleName }} />
            )}
            {activeStep === Steps.Transactions && (
              <CapitalActionTransactions
                hasUncalledEqualisations={hasUncalledEqualisations}
                hasUncalledPartnerTransfers={hasUncalledPartnerTransfers}
                currentPageIndex={currentPageIndex}
                setCurrentPageIndex={setCurrentPageIndex}
                fields={fields}
              />
            )}
            {activeStep === Steps.Review && <CapitalActionReview />}
          </ErrorBoundary>
        </FormWithStateContextProvider>
      </Column>
      <PendingDraftEqualisationsModal
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        onConfirm={handleSubmit}
        loading={isLoading}
      />
      <ConfirmDialog
        open={commitmentsPendingDialog.isOpen}
        onClose={commitmentsPendingDialog.onClose}
        title="Commitments pending close"
        content="There are commitments pending close. If you intend to include these commitments in this capital action, you must close and equalise those commitments before proceeding."
        actions={{
          confirm: {
            label: 'Close and equalise',
            onClick: () => {
              history.push(EQUALISATIONS_ADD);
            },
          },
          cancel: {
            label: 'Ignore and continue with capital action',
            onClick: commitmentsPendingDialog.onClose,
          },
        }}
        maxWidth="sm"
      />
      <ConfirmDialog
        open={draftDeletionDialog.isOpen}
        onClose={() => draftDeletionDialog.onClose()}
        title="Existing draft capital actions"
        content={`There ${draftCapitalActions.length === 1 ? 'is' : 'are'} ${
          draftCapitalActions.length
        } existing capital action${
          draftCapitalActions.length === 1 ? '' : 's'
        } in draft status. Creating a new capital action will permanently delete ${
          draftCapitalActions.length === 1 ? 'it' : 'them'
        }. This cannot be undone.`}
        loading={deleteAllDrafts.isPending}
        actions={{
          confirm: {
            label: 'Continue and delete drafts',
            onClick: () => deleteAllDrafts.mutate({ vehicleId }),
          },
          cancel: {
            label: 'Cancel',
            onClick: () => draftDeletionDialog.onClose(),
          },
        }}
        maxWidth="sm"
      />
    </Page>
  );
};

const AddEditCapitalActionWrapper: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const { id } = useParams<{ id?: string }>();

  const { data: capitalAction, isLoading } = api.capitalAction.get.useQuery(
    { id: id ?? '', vehicleId: vehicle?._id ?? '' },
    { enabled: !!id && !!vehicle },
  );

  if (!vehicle) return null;
  if (id && isLoading) return <PageLoader />;

  return (
    <AddEditCapitalAction
      key={capitalAction?.id ?? 'new'}
      capitalAction={capitalAction}
      vehicleId={vehicle._id}
      vehicleName={vehicle.vehicleName}
    />
  );
};

export default AddEditCapitalActionWrapper;
